import React, { Fragment, useState } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Btn, H2, P } from '../../../AbstractElements';
import {
    EmailAddress, ContinueWithMagicLink, ContinueWithOTP, BackToLogin,
    SendCode, CheckYourEmail, EnterOTPCode, VerifyCode, ResendCode, ResendIn,
    ClickEmailedLink,
} from '../../../Constant';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import API_BASE_URL from '../../../Config/api';
import { completeLogin } from '../../completeLogin';
import useCooldown from '../../useCooldown';

const COOLDOWN_SECONDS = 60;

// Shared UI for both "Continue with Magic Link" and "Continue with OTP":
// either entry point sends the same email (containing both an OTP and a
// magic link), then this screen lets the user finish with the OTP while
// noting the emailed link works too.
const PasswordlessTab = ({ intent, onBack }) => {
    const [step, setStep] = useState('email'); // 'email' | 'sent'
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const { secondsLeft, start } = useCooldown();
    const navigate = useNavigate();

    const heading = intent === 'otp' ? ContinueWithOTP : ContinueWithMagicLink;

    const requestCode = async (e) => {
        e?.preventDefault();
        if (!email) {
            toast.error('Please enter your email address.');
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API_BASE_URL}/api/auth/passwordless/request`, { email });
            toast.success('If an account exists for that email, we\'ve sent a code and login link.');
            setStep('sent');
            start(COOLDOWN_SECONDS);
        } catch (error) {
            const retryAfter = error.response?.data?.retryAfterSeconds;
            if (retryAfter) start(retryAfter);
            toast.error(error.response?.data?.error || 'Could not send the code. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const verifyOtp = async (e) => {
        e.preventDefault();
        if (!otp) {
            toast.error('Please enter the code from your email.');
            return;
        }
        setVerifying(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/auth/passwordless/otp/verify`, { email, otp });
            toast.success('Login Success!');
            completeLogin(navigate, response.data);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Invalid or expired code.');
        } finally {
            setVerifying(false);
        }
    };

    return (
        <Fragment>
            <Form className="theme-form">
                <H2 attrH2={{ className: 'text-center' }}>{heading}</H2>

                {step === 'email' && (
                    <Fragment>
                        <P attrPara={{ className: 'text-center' }}>Enter your email address to continue</P>
                        <FormGroup className='form-group'>
                            <Label className="col-form-label">{EmailAddress}</Label>
                            <Input className="form-control" type="email" value={email} onChange={e => setEmail(e.target.value)} />
                        </FormGroup>
                        <div className='text-end mt-3'>
                            <Btn attrBtn={{ color: 'primary w-100', className: 'btn-block', disabled: sending, onClick: requestCode }}>
                                {sending ? 'Sending...' : SendCode}
                            </Btn>
                        </div>
                    </Fragment>
                )}

                {step === 'sent' && (
                    <Fragment>
                        <P attrPara={{ className: 'text-center' }}>{CheckYourEmail} ({email})</P>
                        <FormGroup className='form-group'>
                            <Label className="col-form-label">{EnterOTPCode}</Label>
                            <Input
                                className="form-control"
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={otp}
                                onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                            />
                        </FormGroup>
                        <div className='text-end mt-3'>
                            <Btn attrBtn={{ color: 'primary w-100', className: 'btn-block', disabled: verifying, onClick: verifyOtp }}>
                                {verifying ? 'Verifying...' : VerifyCode}
                            </Btn>
                        </div>
                        <p className='text-center text-muted mt-3' style={{ fontSize: '0.85rem' }}>{ClickEmailedLink}</p>
                        <div className='text-center mt-2'>
                            <Btn attrBtn={{
                                color: 'link', className: 'p-0', type: 'button',
                                disabled: secondsLeft > 0 || sending,
                                onClick: requestCode,
                            }}>
                                {secondsLeft > 0 ? `${ResendCode} (${ResendIn} 00:${String(secondsLeft).padStart(2, '0')})` : ResendCode}
                            </Btn>
                        </div>
                    </Fragment>
                )}

                <div className='text-center mt-4'>
                    <Btn attrBtn={{ color: 'link', className: 'p-0', type: 'button', onClick: onBack }}>{BackToLogin}</Btn>
                </div>
            </Form>
        </Fragment>
    );
};

export default PasswordlessTab;
